'use client'

import React, { useCallback, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Above everything, including Modal (fixed z-50): dropdown options must never
 * be clipped by or stacked under an overlay.
 */
export const DROPDOWN_Z_INDEX = 9999

export interface DropdownPosition {
  top?: number
  bottom?: number
  left: number
  width: number
}

/**
 * Tracks the fixed-position placement for a dropdown anchored to `anchorRef`.
 * Recomputes on scroll (capture phase, so scrolling ancestors like the Modal
 * body count) and resize. Flips up when there is not enough space below.
 */
export function useDropdownPosition(
  anchorRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean,
  estimatedHeight = 260,
  offset = 4
): DropdownPosition | null {
  const [position, setPosition] = useState<DropdownPosition | null>(null)

  const update = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) {
      return
    }

    const rect = anchor.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const shouldDropUp = spaceBelow < estimatedHeight && rect.top > spaceBelow

    setPosition(
      shouldDropUp
        ? { bottom: window.innerHeight - rect.top + offset, left: rect.left, width: rect.width }
        : { top: rect.bottom + offset, left: rect.left, width: rect.width }
    )
  }, [anchorRef, estimatedHeight, offset])

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null)
      return
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [isOpen, update])

  return position
}

interface DropdownPortalProps {
  position: DropdownPosition | null
  children: React.ReactNode
  className?: string
  /**
   * Exposed so outside-click handlers can whitelist the portal content —
   * it lives under document.body, not under the trigger's DOM subtree.
   */
  portalRef?: React.RefObject<HTMLDivElement | null>
}

/**
 * Renders dropdown content in a portal on document.body with fixed
 * positioning, so no ancestor overflow/stacking context can ever clip it.
 */
export function DropdownPortal({ position, children, className, portalRef }: DropdownPortalProps) {
  if (!position || typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div
      ref={portalRef}
      className={className}
      style={{
        position: 'fixed',
        zIndex: DROPDOWN_Z_INDEX,
        top: position.top,
        bottom: position.bottom,
        left: position.left,
        width: position.width
      }}
    >
      {children}
    </div>,
    document.body
  )
}
